import { useEffect, useId, useRef, useState } from "react"
import styles from "@/styles/components/TextField.module.css"

interface BaseProps {
  label: string
  value: string
  onChange: (event: { target: { value: string } }) => void
  onBlur?: () => void
  placeholder?: string
  autoFocus?: boolean
  required?: boolean
  /**
   * The heavier treatment the artifact gives a form's primary field — the thing
   * being named. Everything else is lighter, so the eye lands on the name first.
   */
  strong?: boolean
}

interface TextInputProps extends BaseProps {
  kind?: "text"
}

interface TextAreaProps extends BaseProps {
  kind: "multiline"
}

interface SelectProps extends BaseProps {
  kind: "select"
  options: readonly string[]
}

type TextFieldProps = TextInputProps | TextAreaProps | SelectProps

export default function TextField(props: TextFieldProps) {
  const id = useId()
  const { label, value, onChange, onBlur, placeholder, autoFocus, required, strong } = props

  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const inputClass = strong ? `${styles.input} ${styles.strong}` : styles.input

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? <span className={styles.required}> *</span> : null}
      </label>

      {props.kind === "multiline" ? (
        <textarea
          id={id}
          className={styles.textarea}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
        />
      ) : props.kind === "select" ? (
        <span className={styles.selectWrap} ref={wrap}>
          <button
            type="button"
            id={id}
            className={open ? `${styles.select} ${styles.selectOpen}` : styles.select}
            onClick={() => setOpen((current) => !current)}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className={value ? styles.selectValue : styles.selectPlaceholder}>
              {value || placeholder}
            </span>
            <svg
              className={open ? `${styles.chevron} ${styles.chevronUp}` : styles.chevron}
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {open ? (
            <span className={styles.menu} role="listbox" aria-labelledby={id}>
              {/* A stored value outside the list is kept as its own option rather
                  than snapped to the nearest. */}
              {value && !props.options.includes(value) ? (
                <SelectOption
                  value={value}
                  selected
                  onPick={() => setOpen(false)}
                  onChange={onChange}
                />
              ) : null}
              {props.options.map((option) => (
                <SelectOption
                  key={option}
                  value={option}
                  selected={option === value}
                  onPick={() => setOpen(false)}
                  onChange={onChange}
                />
              ))}
            </span>
          ) : null}
        </span>
      ) : (
        <input
          id={id}
          className={inputClass}
          type="text"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
      )}
    </div>
  )
}

interface SelectOptionProps {
  value: string
  selected: boolean
  onPick: () => void
  onChange: (event: { target: { value: string } }) => void
}

function SelectOption({ value, selected, onPick, onChange }: SelectOptionProps) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
      onClick={() => {
        onChange({ target: { value } })
        onPick()
      }}
    >
      <span className={styles.optionLabel}>{value}</span>
      {selected ? (
        <span className={styles.tick} aria-hidden="true">
          ✓
        </span>
      ) : null}
    </button>
  )
}
